import React from 'react';
import { ConfigProvider, DatePicker } from 'antd';
import { useController, Control } from 'react-hook-form';
import dayjs from 'dayjs';
import { useTheme, FormControl, InputLabel, Box, useMediaQuery } from '@mui/material';

const { RangePicker } = DatePicker;

interface DateRangePickerProps {
  name: string;
  control: Control<any>;
  /** ISO or YYYY-MM-DD from the current context API (agent / business / agency) for the "Till date" preset */
  createdAt?: string;
  label?: string;
  defaultValue?: string;
  minDate?: string; // YYYY-MM-DD (inclusive)
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  sx?: any;
}

export default function DateRangePicker({
  name,
  control,
  label,
  defaultValue,
  createdAt,
  minDate,
  fullWidth = true,
  required = false,
  disabled = false,
  sx
}: Readonly<DateRangePickerProps>) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [focused, setFocused] = React.useState(false);

  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control,
    defaultValue
  });

  const hasValue = field.value && field.value.length > 0;
  const shrink = focused || hasValue;
  const hasError = !!error && !focused;
  const helperText = !focused ? error?.message : null;

  const parsedValue = React.useMemo(() => {
    if (!field.value) return null;
    const parts = field.value.split(',');
    if (parts.length === 2 && parts[0] && parts[1]) {
      return [dayjs(parts[0]), dayjs(parts[1])] as any;
    }
    return null;
  }, [field.value]);

  const minDateDayjs = React.useMemo(() => {
    if (!minDate) return null;
    const d = dayjs(minDate, 'YYYY-MM-DD', true);
    return d.isValid() ? d : null;
  }, [minDate]);

  const rangePresets = React.useMemo(() => {
    const today = dayjs().startOf('day');
    const yesterday = today.subtract(1, 'day');

    const clampStart = (start: dayjs.Dayjs) =>
      minDateDayjs && start.isBefore(minDateDayjs, 'day') ? minDateDayjs : start;

    const range = (start: dayjs.Dayjs, end: dayjs.Dayjs) => [clampStart(start), end] as [dayjs.Dayjs, dayjs.Dayjs];

    const tillFromCreated = createdAt ? dayjs(createdAt) : null;
    const tillStartBase =
      tillFromCreated && tillFromCreated.isValid() ? tillFromCreated.startOf('day') : today.startOf('year');
    const tillStart = tillStartBase.isAfter(today, 'day') ? today : tillStartBase;

    return [
      { label: 'Yesterday', value: range(yesterday, yesterday) },
      { label: 'Last 7 days', value: range(today.subtract(6, 'day'), today) },
      { label: 'Last 30 days', value: range(today.subtract(29, 'day'), today) },
      { label: 'Last 3 months', value: range(today.subtract(3, 'month'), today) },
      { label: 'Last 6 months', value: range(today.subtract(6, 'month'), today) },
      { label: 'Last 12 months', value: range(today.subtract(1, 'year'), today) },
      { label: 'Till date', value: range(tillStart, today) }
    ];
  }, [minDateDayjs, createdAt]);

  return (
    <FormControl fullWidth={fullWidth} variant='outlined'>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: theme.palette.primary.main,
            colorTextPlaceholder: '#888888',
            borderRadius: 4,
            colorBorder: hasError ? theme.palette.error.main : '#e4e1e1ff',
            colorBgContainer: theme.palette.background.paper,
            colorErrorBorder: theme.palette.error.main,
            colorErrorBorderHover: theme.palette.error.dark
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {label && (
            <InputLabel
              shrink={shrink}
              required={required}
              error={hasError}
              sx={{
                position: 'absolute',
                left: 0,
                top: shrink ? -9 : '50%',
                fontSize: shrink ? '0.75rem' : '1rem',
                backgroundColor: theme.palette.background.paper,
                padding: shrink ? '0 5px' : '0',
                zIndex: 1,
                transition: 'all 0.2s ease-out',
                pointerEvents: 'none',
                transform: shrink ? 'translate(14px, 0) scale(1)' : 'translate(14px, -50%) scale(1)',
                transformOrigin: 'top left',
                color: hasError ? theme.palette.error.main : focused ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.6)'
              }}
            >
              {label}
            </InputLabel>
          )}
          <RangePicker
            format='YYYY-MM-DD'
            ref={field.ref}
            name={field.name}
            value={parsedValue}
            disabled={disabled}
            presets={rangePresets}
            disabledDate={(current) => {
              if (!current) return false;
              if (minDateDayjs && current.isBefore(minDateDayjs, 'day')) return true;
              return false;
            }}
            onChange={(dates) => {
              const formattedDates = dates ? `${dates[0]?.format('YYYY-MM-DD')},${dates[1]?.format('YYYY-MM-DD')}` : '';
              field.onChange(formattedDates);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              field.onBlur();
            }}
            placeholder={focused || hasValue || !label ? ['Start date', 'End date'] : ['', '']}
            size={isLargeScreen ? 'large' : 'middle'}
            status={hasError ? 'error' : undefined}
            style={{
              width: '100%',
              borderColor: hasError ? theme.palette.error.main : focused ? theme.palette.primary.main : undefined,
              borderWidth: focused ? '2px' : '1px',
              ...sx
            }}
            className={`${!focused && !hasValue && label ? 'hide-separator' : ''} ${!focused && !hasError ? 'date-range-picker-unfocused' : ''} ${isMobile ? 'date-range-picker-mobile' : ''}`}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            popupStyle={{
              zIndex: 999999,
              ...(isMobile && {
                maxWidth: '100vw',
                left: '0 !important',
                right: '0',
                margin: '0 auto'
              })
            }}
          />
          {helperText && (
            <Box
              sx={{
                color: hasError ? theme.palette.error.main : 'rgba(0, 0, 0, 0.6)',
                fontSize: '0.75rem',
                marginTop: '3px'
              }}
            >
              {helperText}
            </Box>
          )}
        </Box>
      </ConfigProvider>
    </FormControl>
  );
}
